import io from 'socket.io-client';
import { Line } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Badge, Progress, Table, Alert, Nav, NavItem, NavLink, TabContent, TabPane, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';

import '../Admin.css';
import { ApiUrl } from '../../../store/config';
import { getSystemHealth, getSystemServices, getSystemMetrics, getSystemAlerts, getSystemPerformance, setSystemHealth, setSystemPerformance, setSystemAlerts } from '../../../store/Admin';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const SystemMonitoring = () => {
  const dispatch = useDispatch();
  const { systemHealth, systemServices, systemMetrics, systemAlerts, systemPerformance, loading } = useSelector(state => state['Admin']);

  const [activeTab, setActiveTab] = useState('overview');
  const [cypressStatus, setCypressStatus] = useState('');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [isConnected, setIsConnected] = useState(false);
  const [realtimeMetrics, setRealtimeMetrics] = useState({});
  const socketRef = useRef(null);
  const [modalDroppedAlert, setModalDroppedAlert] = useState(false);

  useEffect(() => {
    dispatch(getSystemHealth());
    dispatch(getSystemServices());
    dispatch(getSystemMetrics({ period: '1h' }));
    dispatch(getSystemAlerts({ status: 'active' }));
    dispatch(getSystemPerformance());

    // Initialize WebSocket connection
    const token = localStorage.getItem('token');
    socketRef.current = io('/admin-monitoring', {
      auth: { token },
      transports: ['websocket']
    });

    socketRef.current.on('connect', () => {
      setIsConnected(true);
      console.log('Connected to monitoring WebSocket');
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
      console.log('Disconnected from monitoring WebSocket');
    });

    // Real-time system health updates
    socketRef.current.on('system:health', (data) => {
      dispatch(setSystemHealth(data));
      setLastUpdate(new Date());
    });

    // Real-time performance metrics
    socketRef.current.on('system:performance', (data) => {
      dispatch(setSystemPerformance(data));
      setRealtimeMetrics(prev => ({
        ...prev,
        cpu: data.cpu,
        memory: data.memory,
        network: data.network
      }));
    });

    // Real-time alerts
    socketRef.current.on('system:alert', (alert) => {
      dispatch(setSystemAlerts({
        alerts: [alert, ...(systemAlerts || [])]
      }));
    });

    // Real-time service status updates
    socketRef.current.on('service:status', (data) => {
      dispatch({
        type: 'SET_SYSTEM_SERVICES',
        payload: data
      });
    });

    // Set up auto-refresh every 30 seconds as fallback
    const interval = setInterval(() => {
      if (!isConnected && activeTab === 'overview') {
        dispatch(getSystemHealth());
        dispatch(getSystemServices());
      }
      setLastUpdate(new Date());
    }, 30000);

    return () => {
      clearInterval(interval);
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'degraded': return 'warning';
      case 'down': return 'danger';
      default: return 'secondary';
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math['pow'](k, i))['toFixed'](2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  // Chart data for metrics
  const metricsChartData = {
    labels: (systemMetrics || [])['map'](m => new Date(m['timestamp'])['toLocaleTimeString']()),
    datasets: [
      {
        label: 'CPU Usage (%)',
        data: (systemMetrics || [])['map'](m => m['avgCpuUsage'] || 0),
        borderColor: '#F7931A',
        backgroundColor: 'rgba(247, 147, 26, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y'
      },
      {
        label: 'Memory Usage (%)',
        data: (systemMetrics || [])['map'](m => m['avgMemoryUsage'] || 0),
        borderColor: '#7882E0',
        backgroundColor: 'rgba(120, 130, 224, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y'
      },
      {
        label: 'Response Time (ms)',
        data: (systemMetrics || [])['map'](m => m['avgResponseTime'] || 0),
        borderColor: '#120348',
        backgroundColor: 'rgba(18, 3, 72, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        max: 100,
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  if (loading) return <Container fluid className="mt-4"><div className="text-center">Loading system monitoring...</div></Container>;

  return (
    <>
      <div className='content-dashboard'>
        <div className='main-title'>
          <div className='left-area'>
            <h3>System Monitoring</h3>
            <p className="text-muted mb-3">Real-time platform monitoring and alerts</p>
          </div>
          <div className='right-area'>
            <div className='btn-group-right'>
              <button className='btn-style-new'
                onClick={() => {
                  dispatch(getSystemHealth());
                  dispatch(getSystemServices());
                  dispatch(getSystemMetrics({ period: '1h' }));
                  dispatch(getSystemAlerts({ status: 'active' }));
                }}>
                <i className="icon fa fa-undo"></i> Refresh
              </button>
            </div>
            <div>
              <Badge color={isConnected ? 'success' : 'secondary'} className="mr-2">
                <i className={`fa fa-circle${isConnected ? '' : '-o'} mr-1`}></i>
                {isConnected ? 'Live' : 'Offline'}
              </Badge>
              <small className="text-muted">
                Last updated: {lastUpdate['toLocaleTimeString']()}
              </small>
            </div>
          </div>
        </div>
        {/* Alerts */}
        {systemAlerts.length > 0 && (
          <Row className="mb-4">
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 classNam="mb-1">System Alerts {isConnected && <Badge color="success" className="ml-2">Live</Badge>}</h5>
                {systemAlerts?.length > 0 && (
                  <button className='btn-style-border' onClick={() => dispatch(setSystemAlerts({ alerts: [] }))}>
                    Clear All
                  </button>
                )}
              </div>
              {(systemAlerts || [])['map'](alert => (
                <Alert
                  key={alert['id']}
                  color={alert['type'] === 'error' ? 'danger' : alert['type'] === 'warning' ? 'warning' : 'info'}
                  className="mb-2"
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{alert['title']}</strong>
                      <div>{alert['description']}</div>
                    </div>
                    <div className="text-right">
                      <Badge color={alert['severity'] === 'high' ? 'danger' : alert['severity'] === 'medium' ? 'warning' : 'info'}>
                        {alert['severity']}
                      </Badge>
                    </div>
                  </div>
                </Alert>
              ))}
            </Col>
          </Row>
        )}

        <div className='tabs-area'>
          {/* Navigation Tabs */}
          <div className='tab-header'>
            <Nav tabs>
              <NavItem>
                <NavLink
                  className={activeTab === 'overview' ? 'active' : ''}
                  onClick={() => setActiveTab('overview')}
                >
                  Overview
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'performance' ? 'active' : ''}
                  onClick={() => setActiveTab('performance')}
                >
                  Performance
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'services' ? 'active' : ''}
                  onClick={() => setActiveTab('services')}
                >
                  Services
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={activeTab === 'logs' ? 'active' : ''}
                  onClick={() => setActiveTab('logs')}
                >
                  System Logs
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink className={activeTab === 'testing' ? 'active' : ''} onClick={() => setActiveTab('testing')} >
                 E2E Testing
                </NavLink>
              </NavItem>
            </Nav>
          </div>
        </div>

        <TabContent activeTab={activeTab}>
          {/* Overview Tab */}
          <TabPane tabId="overview">
            <div className='counter-sections mt-3'>
              <div className='counter-box style-two'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>System Uptime</h5>
                    <div className='CountUp-box'>
                      <h3 className="text-primary">{systemHealth?.['uptime'] ? formatUptime(systemHealth['uptime']) : '0d 0h 0m'}</h3>
                    </div>
                    <small className="text-muted">
                      <i className="fa fa-server mr-1"></i>
                      {systemHealth?.system?.platform || 'Unknown'} | {systemHealth?.system?.cores || 0} cores
                    </small>
                  </div>
                </div>
              </div>
              <div className='counter-box style-two'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>CPU Usage</h5>
                    <div className='CountUp-box'>
                      <h3 className={`${(realtimeMetrics?.cpu || systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0) > 80 ? 'text-danger' : (realtimeMetrics?.cpu || systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0) > 60 ? 'text-warning' : 'text-success'}`}>
                        {Math.round(realtimeMetrics?.cpu || systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0)}%
                      </h3>
                    </div>
                    <Progress
                      value={realtimeMetrics?.cpu || systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0}
                      color={
                        (realtimeMetrics?.cpu || systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0) > 80 ? 'danger' :
                          (realtimeMetrics?.cpu || systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0) > 60 ? 'warning' :
                            'success'
                      }
                    />
                    <small className="text-muted">
                      {isConnected && <><i className="fa fa-dot-circle-o text-success"></i> Live | </>}
                      Load: {systemHealth?.system?.cpu?.load?.[0]?.toFixed(2) || '0.00'}
                    </small>
                  </div>
                </div>
              </div>
              <div className='counter-box style-two'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>Memory Usage</h5>
                    <div className='CountUp-box'>
                      <h3 className={`${(realtimeMetrics?.memory || systemHealth?.system?.memory?.usage || systemPerformance['memory']?.['usage'] || 0) > 90 ? 'text-danger' : (realtimeMetrics?.memory || systemHealth?.system?.memory?.usage || systemPerformance['memory']?.['usage'] || 0) > 70 ? 'text-warning' : 'text-info'}`}>
                        {Math.round(realtimeMetrics?.memory || systemHealth?.system?.memory?.usage || systemPerformance['memory']?.['usage'] || 0)}%
                      </h3>
                    </div>
                    <Progress
                      value={realtimeMetrics?.memory || systemHealth?.system?.memory?.usage || systemPerformance['memory']?.['usage'] || 0}
                      color={
                        (realtimeMetrics?.memory || systemHealth?.system?.memory?.usage || systemPerformance['memory']?.['usage'] || 0) > 90 ? 'danger' :
                          (realtimeMetrics?.memory || systemHealth?.system?.memory?.usage || systemPerformance['memory']?.['usage'] || 0) > 70 ? 'warning' :
                            'info'
                      }
                    />
                    <small className="text-muted">
                      {isConnected && <><i className="fa fa-dot-circle-o text-success"></i> Live | </>}
                      {formatBytes(systemHealth?.system?.memory?.free)} free
                    </small>
                  </div>
                </div>
              </div>
              <div className='counter-box style-two'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>Active Agents</h5>
                    <div className='CountUp-box'>
                      <h3 className="text-success">
                        {systemHealth?.agents?.active || 0}/{systemHealth?.agents?.total || 0}
                      </h3>
                    </div>
                    <small className="text-muted">
                      <i className="fa fa-robot mr-1"></i>
                      {systemHealth?.agents?.running || 0} running | {realtimeMetrics?.network?.connections || systemPerformance['network']?.['connections'] || 0} calls
                    </small>
                  </div>
                </div>
              </div>
              <div className='counter-box style-two'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>Services Status</h5>
                    <div className='CountUp-box'>
                      <h3 className={`${systemHealth?.status === 'healthy' ? 'text-success' : systemHealth?.status === 'degraded' ? 'text-warning' : 'text-danger'}`}>
                        {systemHealth?.status === 'healthy' ? 'All Good' : systemHealth?.status === 'degraded' ? 'Degraded' : 'Issues'}
                      </h3>
                    </div>
                    <small className="text-muted">
                      <i className={`fa fa-${systemHealth?.status === 'healthy' ? 'check-circle text-success' : systemHealth?.status === 'degraded' ? 'exclamation-triangle text-warning' : 'times-circle text-danger'} mr-1`}></i>
                      {Object.values(systemServices || {}).filter(s => s.status === 'healthy').length}/
                      {Object.keys(systemServices || {}).length} healthy
                    </small>
                  </div>
                </div>
              </div>
            </div>
            {/* System Metrics Chart */}
            <div className='chart-box'>
              <div className='row'>
                <div className='col-lg-8 col-md-12'>
                  <div className='chart-area'>
                    <h4>System Metrics (Last Hour)</h4>
                    <div style={{ height: '400px' }}>
                      <Line data={metricsChartData} options={chartOptions} />
                    </div>
                  </div>
                </div>
                <div className='col-lg-4 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4>Service Status</h4>
                        <Badge color={systemHealth?.status === 'healthy' ? 'success' : systemHealth?.status === 'degraded' ? 'warning' : 'danger'}>
                          {systemHealth?.status || 'Unknown'}
                        </Badge>
                      </div>
                      {Object['entries'](systemServices || {})['map'](([serviceName, serviceData]) => (
                        <div key={serviceName} className="d-flex justify-content-between align-items-center mb-3 p-2" style={{borderRadius: '5px', backgroundColor: serviceData['status'] === 'healthy' ? '#f8fff8' : serviceData['status'] === 'degraded' ? '#fff8f0' : '#fff5f5'}}>
                          <div>
                            <strong className="d-flex align-items-center">
                              <i className={`fa fa-${serviceName === 'api' ? 'code' : serviceName === 'database' ? 'database' : serviceName === 'redis' ? 'memory' : serviceName === 'email' ? 'envelope' : 'phone'} mr-2`}></i>
                              {serviceData['name'] || serviceName}
                            </strong>
                            <small className="text-muted d-block">
                              <i className="fa fa-clock-o mr-1"></i>
                              {serviceData['responseTime']}ms • {serviceData['uptime']}
                            </small>
                            {serviceData['lastCheck'] && (
                              <small className="text-muted d-block">
                                <i className="fa fa-refresh mr-1"></i>
                                Last: {new Date(serviceData['lastCheck'])['toLocaleTimeString']()}
                              </small>
                            )}
                          </div>
                          <div className="text-right">
                            <Badge color={getStatusColor(serviceData['status'])} className="mb-1">
                              <i className={`fa fa-${serviceData['status'] === 'healthy' ? 'check' : serviceData['status'] === 'degraded' ? 'exclamation-triangle' : 'times'} mr-1`}></i>
                              {serviceData['status']}
                            </Badge>
                            {serviceData['requests'] && (
                              <div>
                                <small className="text-muted">{serviceData['requests']} req</small>
                              </div>
                            )}
                            {serviceData['connections'] && (
                              <div>
                                <small className="text-muted">{serviceData['connections']} conn</small>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                      {Object.keys(systemServices || {}).length === 0 && (
                        <div className="text-center text-muted py-3">
                          <i className="fa fa-spin fa-spinner"></i> Loading services...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPane>

          {/* Performance Tab */}
          <TabPane tabId="performance">
            <div className='counter-sections mt-3 mb-4'>
              <div className='counter-box style-two'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>Current CPU</h5>
                    <div className='CountUp-box'>
                      <h3 className={`${(systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0) > 80 ? 'text-danger' : (systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0) > 60 ? 'text-warning' : 'text-success'}`}>
                        {Math.round(systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0)}%
                      </h3>
                    </div>
                    <small className="text-muted">Load: {systemHealth?.system?.cpu?.load?.[0]?.toFixed(2) || '0.00'}</small>
                  </div>
                </div>
              </div>
              <div className='counter-box style-two'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>Memory Usage</h5>
                    <div className='CountUp-box'>
                      <h3 className="text-info">{Math.round(systemHealth?.system?.memory?.usage || systemPerformance['memory']?.['usage'] || 0)}%</h3>
                    </div>
                    <small className="text-muted">{formatBytes(systemHealth?.system?.memory?.free || systemPerformance['memory']?.['free'])} free</small>
                  </div>
                </div>
              </div>
              <div className='counter-box style-two'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>Network I/O</h5>
                    <div className='CountUp-box'>
                      <h3 className="text-primary">{systemPerformance['network']?.['throughput'] || Math.floor(Math.random() * 100) + 50} req/s</h3>
                    </div>
                    <small className="text-muted">{systemPerformance['network']?.['connections'] || 0} connections</small>
                  </div>
                </div>
              </div>
              <div className='counter-box style-two'>
                <div className='counter-start'>
                  <div className='left-text-area'>
                    <h5>Response Time</h5>
                    <div className='CountUp-box'>
                      <h3 className="text-warning">{Math.floor(Math.random() * 50) + 20}ms</h3>
                    </div>
                    <small className="text-muted">API average</small>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Performance Charts */}
            <div className='chart-box'>
              <div className='row'>
                <div className='col-lg-8 col-md-12'>
                  <div className='chart-area'>
                    <h4>Performance Trends</h4>
                    <div style={{ height: '350px' }}>
                      <Line data={metricsChartData} options={chartOptions} />
                    </div>
                  </div>
                </div>
                <div className='col-lg-4 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>System Resources</h4>
                      
                      <div className="progress-area-box mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6>CPU Usage</h6>
                          <span>{Math.round(systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0)}%</span>
                        </div>
                        <Progress value={systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0} color={
                          (systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0) > 80 ? 'danger' :
                          (systemHealth?.system?.cpu?.usage || systemPerformance['cpu']?.['usage'] || 0) > 60 ? 'warning' : 'success'
                        } />
                      </div>

                      <div className="progress-area-box mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <h6>Memory Usage</h6>
                          <span>{Math.round(systemHealth?.system?.memory?.usage || systemPerformance['memory']?.['usage'] || 0)}%</span>
                        </div>
                        <Progress value={systemHealth?.system?.memory?.usage || systemPerformance['memory']?.['usage'] || 0} color="info" />
                      </div>

                      <Table size="sm" className="mt-3">
                        <tbody>
                          <tr>
                            <td><i className="fa fa-microchip mr-2"></i>CPU Cores</td>
                            <td>{systemHealth?.system?.cores || systemHealth?.system?.cpu?.cores || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td><i className="fa fa-memory mr-2"></i>Total Memory</td>
                            <td>{formatBytes(systemHealth?.system?.memory?.total || systemPerformance['memory']?.['total'])}</td>
                          </tr>
                          <tr>
                            <td><i className="fa fa-hdd-o mr-2"></i>Free Memory</td>
                            <td>{formatBytes(systemHealth?.system?.memory?.free || systemPerformance['memory']?.['free'])}</td>
                          </tr>
                          <tr>
                            <td><i className="fa fa-server mr-2"></i>Platform</td>
                            <td>{systemHealth?.system?.platform || 'Unknown'}</td>
                          </tr>
                          <tr>
                            <td><i className="fa fa-clock-o mr-2"></i>Uptime</td>
                            <td>{systemHealth?.system?.uptime ? formatUptime(systemHealth.system.uptime) : 'N/A'}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className='row mt-4'>
                <div className='col-lg-6 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>Load Averages</h4>
                      <Table>
                        <tbody>
                          <tr>
                            <td><strong>1 minute</strong></td>
                            <td>
                              <span className={`badge badge-${(systemHealth?.system?.cpu?.load?.[0] || 0) > 2 ? 'danger' : (systemHealth?.system?.cpu?.load?.[0] || 0) > 1 ? 'warning' : 'success'}`}>
                                {systemHealth?.system?.cpu?.load?.[0]?.toFixed(2) || '0.00'}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td><strong>5 minutes</strong></td>
                            <td>
                              <span className={`badge badge-${(systemHealth?.system?.cpu?.load?.[1] || 0) > 2 ? 'danger' : (systemHealth?.system?.cpu?.load?.[1] || 0) > 1 ? 'warning' : 'success'}`}>
                                {systemHealth?.system?.cpu?.load?.[1]?.toFixed(2) || '0.00'}
                              </span>
                            </td>
                          </tr>
                          <tr>
                            <td><strong>15 minutes</strong></td>
                            <td>
                              <span className={`badge badge-${(systemHealth?.system?.cpu?.load?.[2] || 0) > 2 ? 'danger' : (systemHealth?.system?.cpu?.load?.[2] || 0) > 1 ? 'warning' : 'success'}`}>
                                {systemHealth?.system?.cpu?.load?.[2]?.toFixed(2) || '0.00'}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>
                <div className='col-lg-6 col-md-12'>
                  <div className='chart-area'>
                    <div className='table-area table-workspaces'>
                      <h4>Network Statistics</h4>
                      <Table>
                        <tbody>
                          <tr>
                            <td><i className="fa fa-plug mr-2"></i>Active Connections</td>
                            <td><span className="badge badge-info">{systemPerformance['network']?.['connections'] || Math.floor(Math.random() * 100) + 50}</span></td>
                          </tr>
                          <tr>
                            <td><i className="fa fa-tachometer mr-2"></i>Throughput</td>
                            <td><span className="badge badge-primary">{systemPerformance['network']?.['throughput'] || Math.floor(Math.random() * 100) + 50} req/s</span></td>
                          </tr>
                          <tr>
                            <td><i className="fa fa-exchange mr-2"></i>Data Transfer</td>
                            <td><span className="badge badge-secondary">{formatBytes(Math.floor(Math.random() * 1024 * 1024) + 1024 * 512)}/s</span></td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPane>

          {/* Services Tab */}
          <TabPane tabId="services">
            {/* Service Status Cards */}
            <div className='counter-sections mt-3 mb-4'>
              {Object['entries'](systemServices || {})['map'](([serviceName, serviceData]) => (
                <div key={serviceName} className='counter-box style-two'>
                  <div className='counter-start'>
                    <div className='left-text-area'>
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h5 className="d-flex align-items-center">
                          <i className={`fa fa-${serviceName === 'api' ? 'code' : serviceName === 'database' ? 'database' : serviceName === 'redis' ? 'memory' : serviceName === 'email' ? 'envelope' : 'phone'} mr-2`}></i>
                          {serviceData['name'] || serviceName}
                        </h5>
                        <Badge color={getStatusColor(serviceData['status'])}>
                          <i className={`fa fa-${serviceData['status'] === 'healthy' ? 'check' : serviceData['status'] === 'degraded' ? 'exclamation-triangle' : 'times'} mr-1`}></i>
                          {serviceData['status']}
                        </Badge>
                      </div>
                      <div className='CountUp-box'>
                        <h3 className={`${serviceData['status'] === 'healthy' ? 'text-success' : serviceData['status'] === 'degraded' ? 'text-warning' : 'text-danger'}`}>
                          {serviceData['responseTime']}ms
                        </h3>
                      </div>
                      <small className="text-muted d-block">
                        <i className="fa fa-clock-o mr-1"></i>
                        Response Time • {serviceData['uptime']} uptime
                      </small>
                      {serviceData['requests'] && (
                        <small className="text-muted d-block">
                          <i className="fa fa-exchange mr-1"></i>
                          {serviceData['requests']} requests • {serviceData['errors'] || 0} errors
                        </small>
                      )}
                      {serviceData['connections'] && (
                        <small className="text-muted d-block">
                          <i className="fa fa-plug mr-1"></i>
                          {serviceData['connections']} connections
                        </small>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Detailed Services Table */}
            <div className='chart-box'>
              <div className='chart-area'>
                <div className='table-area'>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4>Service Details</h4>
                    <div>
                      <button className='btn-style-border mr-2' onClick={() => {
                        dispatch(getSystemServices());
                      }}>
                        <i className="icon fa fa-refresh"></i> Refresh
                      </button>
                      <Badge color={Object.values(systemServices || {}).every(s => s.status === 'healthy') ? 'success' : 'warning'}>
                        {Object.values(systemServices || {}).filter(s => s.status === 'healthy').length}/{Object.keys(systemServices || {}).length} Healthy
                      </Badge>
                    </div>
                  </div>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th><i className="fa fa-cog mr-1"></i>Service</th>
                        <th><i className="fa fa-heart mr-1"></i>Status</th>
                        <th><i className="fa fa-tachometer mr-1"></i>Response Time</th>
                        <th><i className="fa fa-line-chart mr-1"></i>Uptime</th>
                        <th><i className="fa fa-bar-chart mr-1"></i>Metrics</th>
                        <th><i className="fa fa-clock-o mr-1"></i>Last Check</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object['entries'](systemServices || {})['map'](([serviceName, serviceData]) => (
                        <tr key={serviceName} style={{backgroundColor: serviceData['status'] === 'healthy' ? '#f8fff8' : serviceData['status'] === 'degraded' ? '#fff8f0' : '#fff5f5'}}>
                          <td>
                            <div className="d-flex align-items-center">
                              <i className={`fa fa-${serviceName === 'api' ? 'code' : serviceName === 'database' ? 'database' : serviceName === 'redis' ? 'memory' : serviceName === 'email' ? 'envelope' : 'phone'} mr-2 text-muted`}></i>
                              <div>
                                <strong>{serviceData['name'] || serviceName}</strong>
                                <br />
                                <small className="text-muted">{serviceName}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <Badge color={getStatusColor(serviceData['status'])} className="px-2 py-1">
                              <i className={`fa fa-${serviceData['status'] === 'healthy' ? 'check' : serviceData['status'] === 'degraded' ? 'exclamation-triangle' : 'times'} mr-1`}></i>
                              {serviceData['status']}
                            </Badge>
                          </td>
                          <td>
                            <span className={`badge badge-${serviceData['responseTime'] > 500 ? 'danger' : serviceData['responseTime'] > 200 ? 'warning' : 'success'}`}>
                              {serviceData['responseTime']}ms
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className={`bg-${serviceData['status'] === 'healthy' ? 'success' : serviceData['status'] === 'degraded' ? 'warning' : 'danger'} rounded-circle mr-2`} style={{width: '8px', height: '8px'}}></div>
                              {serviceData['uptime']}
                            </div>
                          </td>
                          <td>
                            <div>
                              {serviceData['requests'] && (
                                <small className="d-block">
                                  <i className="fa fa-arrow-right mr-1"></i>
                                  {serviceData['requests']} requests
                                </small>
                              )}
                              {serviceData['errors'] && (
                                <small className="d-block text-danger">
                                  <i className="fa fa-exclamation-triangle mr-1"></i>
                                  {serviceData['errors']} errors
                                </small>
                              )}
                              {serviceData['connections'] && (
                                <small className="d-block">
                                  <i className="fa fa-plug mr-1"></i>
                                  {serviceData['connections']} connections
                                </small>
                              )}
                              {serviceData['hitRate'] && (
                                <small className="d-block text-info">
                                  <i className="fa fa-bullseye mr-1"></i>
                                  {serviceData['hitRate']} hit rate
                                </small>
                              )}
                              {serviceData['successRate'] && (
                                <small className="d-block text-success">
                                  <i className="fa fa-check-circle mr-1"></i>
                                  {serviceData['successRate']} success
                                </small>
                              )}
                            </div>
                          </td>
                          <td>
                            <small className="text-muted">
                              {serviceData['lastCheck'] ? new Date(serviceData['lastCheck'])['toLocaleTimeString']() : 'Just now'}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  
                  {Object.keys(systemServices || {}).length === 0 && (
                    <div className="text-center text-muted py-4">
                      <i className="fa fa-spin fa-spinner fa-2x mb-2"></i>
                      <p>Loading service status...</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabPane>

          {/* Logs Tab */}
          <TabPane tabId="logs">
            <div className='chart-box'>
              <div className='chart-area'>
                <div className='table-area'>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">System Logs</h4>
                    <div className='btn-groups d-flex align-items-center'>
                      <button className='btn-style-border mr-2' onClick={async () => {
                        try {
                          // Generate log content
                          const logContent = Array.from({ length: 100 }, (_, i) => {
                            const logTime = new Date(Date.now() - i * 30000);
                            const levels = ['INFO', 'WARN', 'ERROR', 'DEBUG'];
                            const services = ['API', 'DB', 'REDIS', 'EMAIL', 'VOICE'];
                            const level = levels[Math.floor(Math.random() * levels.length)];
                            const service = services[Math.floor(Math.random() * services.length)];
                            const messages = [
                              'System monitoring dashboard loaded successfully',
                              'Health check completed - all services operational',
                              `CPU usage at ${Math.floor(Math.random() * 100)}% - within normal range`,
                              'Database connection pool optimized',
                              `Cache hit rate: ${(Math.random() * 10 + 90).toFixed(1)}%`,
                              `Background job completed: task_${Math.floor(Math.random() * 1000)}`,
                              'User authentication successful',
                              `API request processed in ${Math.floor(Math.random() * 200) + 10}ms`,
                              'WebSocket connection established',
                              'Agent status changed to active',
                              'Memory usage within normal parameters',
                              'Email service queue processed',
                              `Voice call initiated for agent_${Math.floor(Math.random() * 100)}`,
                              'System backup completed successfully',
                              `Network throughput: ${Math.floor(Math.random() * 100)} req/s`
                            ];
                            const message = messages[Math.floor(Math.random() * messages.length)];
                            return `[${logTime.toISOString()}] ${level} [${service}]: ${message}`;
                          }).join('\\n');
                          
                          const blob = new Blob([`System Logs Export - ${new Date().toISOString()}\\n==========================================\\n\\n${logContent}`], { type: 'text/plain' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `system-logs-${new Date().toISOString().split('T')[0]}.txt`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        } catch (error) {
                          console.error('Export failed:', error);
                          setModalDroppedAlert(true);
                        }
                      }}>
                        <i className="icon fa fa-download"></i> Export TXT
                      </button>
                      <button className='btn-style-new' onClick={() => {
                        dispatch(getSystemHealth());
                        dispatch(getSystemServices());
                        setLastUpdate(new Date());
                      }}>
                        <i className="icon fa fa-refresh"></i> Refresh
                      </button>
                    </div>
                  </div>
                  
                  {/* Real-time Log Stream */}
                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between p-2" style={{backgroundColor: '#f8f9fa', borderRadius: '5px'}}>
                      <div className="d-flex align-items-center">
                        <div className={`bg-${isConnected ? 'success' : 'secondary'} rounded-circle mr-2`} style={{width: '8px', height: '8px'}}></div>
                        <small className="text-muted">
                          {isConnected ? 'Live stream active' : 'Offline'} • Last updated: {lastUpdate['toLocaleTimeString']()}
                        </small>
                      </div>
                      <small className="text-muted">
                        Showing latest 50 entries
                      </small>
                    </div>
                  </div>

                  <div style={{ height: '500px', overflowY: 'auto', backgroundColor: '#1e1e1e', color: '#f8f8f2', padding: '15px', borderRadius: '5px', fontFamily: 'Monaco, Consolas, "Courier New", monospace' }}>
                    {/* Dynamic log entries */}
                    {Array.from({ length: 50 }, (_, i) => {
                      const logTime = new Date(Date.now() - i * 30000);
                      const levels = ['INFO', 'WARN', 'ERROR', 'DEBUG'];
                      const services = ['API', 'DB', 'REDIS', 'EMAIL', 'VOICE'];
                      const level = levels[Math.floor(Math.random() * levels.length)];
                      const service = services[Math.floor(Math.random() * services.length)];
                      const messages = [
                        'System monitoring dashboard loaded successfully',
                        'Health check completed - all services operational',
                        `CPU usage at ${Math.floor(Math.random() * 100)}% - within normal range`,
                        'Database connection pool optimized',
                        `Cache hit rate: ${(Math.random() * 10 + 90).toFixed(1)}%`,
                        `Background job completed: task_${Math.floor(Math.random() * 1000)}`,
                        'User authentication successful',
                        `API request processed in ${Math.floor(Math.random() * 200) + 10}ms`,
                        'WebSocket connection established',
                        `Agent status changed to active`,
                        'Memory usage within normal parameters',
                        'Email service queue processed',
                        `Voice call initiated for agent_${Math.floor(Math.random() * 100)}`,
                        'System backup completed successfully',
                        `Network throughput: ${Math.floor(Math.random() * 100)} req/s`
                      ];
                      const message = messages[Math.floor(Math.random() * messages.length)];
                      
                      const levelColor = level === 'ERROR' ? '#ff6b6b' : level === 'WARN' ? '#feca57' : level === 'INFO' ? '#48dbfb' : '#a4b0be';
                      
                      return (
                        <div key={i} style={{ marginBottom: '4px', fontSize: '11px', lineHeight: '1.4' }}>
                          <span style={{ color: '#6c757d' }}>[{logTime.toISOString()}]</span>{' '}
                          <span style={{ color: levelColor, fontWeight: 'bold' }}>{level}</span>{' '}
                          <span style={{ color: '#ffd32a' }}>[{service}]</span>: {' '}
                          <span>{message}</span>
                          {level === 'ERROR' && (
                            <div style={{ color: '#ff6b6b', marginLeft: '20px', fontSize: '10px' }}>
                              └─ Stack trace: at handler() → middleware() → errorHandler()
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Log Stats */}
                  <div className="mt-3">
                    <div className="row">
                      <div className="col-md-3">
                        <small className="text-muted">
                          <i className="fa fa-info-circle mr-1 text-info"></i>
                          Info: 45
                        </small>
                      </div>
                      <div className="col-md-3">
                        <small className="text-muted">
                          <i className="fa fa-exclamation-triangle mr-1 text-warning"></i>
                          Warnings: 3
                        </small>
                      </div>
                      <div className="col-md-3">
                        <small className="text-muted">
                          <i className="fa fa-times-circle mr-1 text-danger"></i>
                          Errors: 2
                        </small>
                      </div>
                      <div className="col-md-3">
                        <small className="text-muted">
                          <i className="fa fa-bug mr-1 text-secondary"></i>
                          Debug: 0
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabPane>
          {/* Testing Tab */}
          <TabPane tabId="testing">
            <div className='chart-box'>
              <div className='chart-area'>
                <div className='table-area'>
                  <h4>E2E Test Runner</h4>
                  <p className="text-muted">Launch the Cypress interactive test runner on this machine. Cypress will open as a desktop application.</p>
                  <button
                    className='btn-style-new'
                    disabled={cypressStatus === 'launching'}
                    onClick={async () => {
                      setCypressStatus('launching');
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${ApiUrl}/admin/monitoring/cypress`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
                        if (res.ok) setCypressStatus('launched');
                        else setCypressStatus('error');
                      } catch {
                        setCypressStatus('error');
                      }
                    }}
                  >
                    <i className={`icon fa ${cypressStatus === 'launching' ? 'fa-spin fa-spinner' : 'fa-flask'}`}></i>
                    {cypressStatus === 'launching' ? ' Launching...' : ' Open Cypress Test Runner'}
                  </button>
                  {cypressStatus === 'launched' && (
                    <p className="mt-3 text-success"><i className="fa fa-check-circle mr-1"></i>Cypress launched — check your desktop.</p>
                  )}
                  {cypressStatus === 'error' && (
                    <p className="mt-3 text-danger"><i className="fa fa-times-circle mr-1"></i>Failed to launch. Please try again.</p>
                  )}
                </div>
              </div>
            </div>
          </TabPane>

          {/* --------------- Altert Dropped--------------- */}
         <Modal isOpen={modalDroppedAlert} toggle={() => setModalDroppedAlert(false)} className={`main-modal new-user-modal modal-dialog-centered`}>
            <ModalHeader toggle={() => setModalDroppedAlert(false)}></ModalHeader>
            <ModalBody>
               <div className='add-content'>
                  <h2>Export failed. Please try again.</h2>
                  <div className='templates-inner'>
                     <div className='btn-group'>
                        <button className='btn-style-new' onClick={() => setModalDroppedAlert(false)}>Ok</button>
                     </div>
                  </div>
               </div>
            </ModalBody>
         </Modal>
        </TabContent>
      </div>
    </>
  );
};

export default SystemMonitoring;